<template>

    <Head title="Supplier Groups"/>

    <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">Supplier Groups</h1>

    <SettingsTabs/>

    <div class="bg-white py-6 px-4 sm:p-6 shadow">
        <div class="flex justify-end">
            <search-filter :show-filter="false" v-model:value="form.search" class="w-1/4 max-w-md mr-4"
                           @reset="reset"></search-filter>
            <Button v-on:click="toggleCreateSupplierGroupSlideOut"
                    class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2">
                <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Add New Supplier Group
            </Button>
        </div>
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th scope="col" class="relative px-6 py-3">
                                    <span class="sr-only">Edit</span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                                <tr v-if="supplier_groups.data.length > 0" v-for="(supplier_group, index) in supplier_groups.data" :key="index"
                                    :class="{'bg-gray-100': index % 2}" class="hover:bg-gray-50 cursor-pointer">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                        @click="editSupplierGroupSlideout(supplier_group)">
                                        {{ supplier_group.name }}
                                    </td>
                                    <td class="px-6  py-4 whitespace-nowrap text-right text-sm">
                                        <span class="text-red-600 hover:text-red-900 float-right"
                                            @click="deleteSupplierGroup(supplier_group)">
                                            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                            </svg>
                                        </span>
                                    </td>
                                </tr>
                                <tr v-else>
                                    <td colspan="6" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">
                                        There are no supplier groups available for your search criteria.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Pagination :links="supplier_groups.links"/>
        </div>
    </div>
    <CreateSupplierGroup ref="createSupplierGroupSlideOut"/>
</template>


<script>

import {Head} from "@inertiajs/vue3";
import SettingsTabs from "@/Components/SettingsTabs.vue";
import Authenticated from "@/Layouts/Authenticated.vue";
import CreateCountry from "@/Components/SlideOuts/CreateCountry";
import { router } from '@inertiajs/vue3';
import Pagination from "@/Components/Pagination.vue";
import SearchFilter from "@/Components/SearchFilter.vue";
import throttle from 'lodash/throttle'
import debounce from 'lodash/debounce'
import pickBy from 'lodash/pickBy'
import mapValues from 'lodash/mapValues'
import CreateRegions from "@/Components/SlideOuts/CreateRegions";
import CreateVATRates from "@/Components/SlideOuts/CreateVATRates";
import {PlusSmIcon as PlusSmIconSolid} from '@heroicons/vue/solid'
import Button from "@/Components/Button.vue";
import CreateSupplierGroup from "@/Components/SlideOuts/CreateSupplierGroup.vue";

export default {
    layout: Authenticated,
    components: {
        CreateSupplierGroup,
        Pagination,
        SettingsTabs,
        Head,
        SearchFilter,
        PlusSmIconSolid,
        Button
    },
    props: {
        supplier_groups: Object
    },
    data() {
        return {
            form: {
                search: null
            }
        }
    },
    methods: {
        toggleCreateSupplierGroupSlideOut() {
            this.$refs.createSupplierGroupSlideOut.updating = false;
            this.$refs.createSupplierGroupSlideOut.open();
        },
        editSupplierGroupSlideout(supplier_group) {
            this.$refs.createSupplierGroupSlideOut.updating = true;
            this.$refs.createSupplierGroupSlideOut.form.id = supplier_group.id;
            this.$refs.createSupplierGroupSlideOut.form.name = supplier_group.name;
            this.$refs.createSupplierGroupSlideOut.open();
        },
        deleteSupplierGroup(id) {
            this.$swal.fire({
                title: 'Remove Supplier Group',
                html: "You are removing a supplier group. <br/>Are you sure you'd like to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        this.$inertia.delete(route('admin.settings.supplier-groups.supplier-group.delete', id), {
                            preserveScroll: true
                        });
                    }
                });
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function () {
                this.$inertia.get(route('admin.settings.supplier-groups.index'), pickBy(this.form), {preserveState: true})
            }, 500),
        },
    },

}
</script>
